import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useEndpointFetch } from '../../../../../global/network/useEndpointFetch';

type ChatSettingsPayload = {
  chatEnabled: boolean;
  chatAutoChannelProgram: boolean;
  chatAutoChannelActivity: boolean;
  chatAutoChannelRole: boolean;
};

export type ChatAutoChannelEntityType = 'program' | 'activity' | 'role';

type EntityTypePayload = { entityType: ChatAutoChannelEntityType };

const useSaveChatSettings = () => {
  return useEndpointFetch<ChatSettingsPayload>(`/tm/config/chat-settings`);
};

const useBackfillChatChannels = () => {
  return useEndpointFetch<EntityTypePayload>(`/vm/chat/channel/backfill`);
};

const useAutoArchiveChatChannels = () => {
  return useEndpointFetch<EntityTypePayload>(`/vm/chat/channel/auto-archive`);
};

const ENTITY_LABEL: Record<ChatAutoChannelEntityType, string> = {
  program: 'program',
  activity: 'activity',
  role: 'role',
};

export const useAdminConsoleChatActions = () => {
  const saveChatSettings = useSaveChatSettings();
  const backfillChannels = useBackfillChatChannels();
  const autoArchiveChannels = useAutoArchiveChatChannels();
  const { setSnackbar } = useSnackbar();

  const runSaveChatSettings = (payload: ChatSettingsPayload & { handleSuccess?: () => void }) => {
    const { handleSuccess, ...settings } = payload;
    return saveChatSettings
      .run(settings)
      .then((res) => {
        if (!res.ok) {
          setSnackbar({ open: true, variant: 'error', message: 'Unable to update chat settings' });
          return;
        }
        setSnackbar({ open: true, variant: 'success', message: 'Chat settings updated successfully' });
        if (handleSuccess) handleSuccess();
      })
      .catch(() =>
        setSnackbar({ open: true, variant: 'error', message: 'Unable to update chat settings' })
      );
  };

  const runBackfillChannels = (entityType: ChatAutoChannelEntityType) => {
    return backfillChannels
      .run({ entityType })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({ open: true, variant: 'error', message: `Unable to backfill ${ENTITY_LABEL[entityType]} channels` });
          return;
        }
        setSnackbar({
          open: true,
          variant: 'success',
          message: `Backfilling ${ENTITY_LABEL[entityType]} channels in the background`,
        });
      })
      .catch(() =>
        setSnackbar({ open: true, variant: 'error', message: `Unable to backfill ${ENTITY_LABEL[entityType]} channels` })
      );
  };

  const runAutoArchiveChannels = (entityType: ChatAutoChannelEntityType) => {
    return autoArchiveChannels
      .run({ entityType })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({ open: true, variant: 'error', message: `Unable to archive ${ENTITY_LABEL[entityType]} channels` });
          return;
        }
        setSnackbar({
          open: true,
          variant: 'success',
          message: `Archiving ${ENTITY_LABEL[entityType]} channels in the background`,
        });
      })
      .catch(() =>
        setSnackbar({ open: true, variant: 'error', message: `Unable to archive ${ENTITY_LABEL[entityType]} channels` })
      );
  };

  return {
    runSaveChatSettings,
    runSaveChatSettingsLoading: saveChatSettings.isLoading,
    runBackfillChannels,
    runBackfillChannelsLoading: backfillChannels.isLoading,
    runAutoArchiveChannels,
    runAutoArchiveChannelsLoading: autoArchiveChannels.isLoading,
  };
};
